/* eslint-disable import/no-extraneous-dependencies */
import { IconChevronLeft, IconChevronRight, IconExternalLink, IconLink } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { Skeleton } from '@mui/material';
import { toast } from 'react-toastify';
import EmptyCommentsIcon from '../../../../assets/icons/EmptyCommentsIcon';
import TrashIcon from '../../../../assets/icons/trash-icon';
import { deleteContentSubmission } from '../../../../services/dashboard.service';
import { Comment, ContentSubmission, MediaUrl } from '../../../../types/content-submissions';
import CustomModal from '../../../common/custom-modal/custom-modal';
import { Button } from '../../../styledComponents';
import CommentThread from '../comment-thread/comment-thread';
import DeleteModal from '../delete-modal/delete-modal';
import styles from './view-modal.module.css';
import TextButton from '../../../common/TextButton/TextButton';
import ButtonComponent from '../../../common/button-component/button-component';
import ResubmissionTag from '../resubmission-tag/resubmission-tag';

interface ViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResubmit: () => void;
  submission: ContentSubmission | null;
  comments: Comment[];
  isLoadingComments: boolean;
}

export default function ViewModal({
  isOpen,
  onClose,
  onResubmit,
  submission,
  comments,
  isLoadingComments,
}: ViewModalProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Delete mutation
  const deleteSubmissionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteContentSubmission(id);
      if (!response.status) {
        throw new Error(response.message || 'Failed to delete submission');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      setIsDeleteModalOpen(false);
      onClose();
      toast.success('Submission deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error deleting submission');
    },
  });

  const handleDeleteConfirm = () => {
    if (!submission) return;
    deleteSubmissionMutation.mutate(submission._id);
  };

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    setSelectedIndex(newIndex);
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!isOpen || !submission) return null;

  const renderMediaContent = (url: MediaUrl, index: number) => {
    if (!url?.fileUrl) {
      return (
        <div className={styles.linkPreviewContainer}>
          <div className={styles.linkIcon}>
            <IconLink size={48} />
          </div>
          <p className={styles.errorText}>Invalid URL</p>
        </div>
      );
    }

    if (url.type.includes('video')) {
      // For video content, use the player but also have thumbnail available
      return (
        <video slot="media" src={url.fileUrl} controls className={styles.media} playsInline crossOrigin="anonymous">
          <track kind="captions" />
        </video>
      );
    }

    return (
      <img
        src={url.fileUrl || '/images/default-thumbnail.jpeg'}
        alt={`Content ${index + 1}`}
        className={styles.media}
        onError={(e) => {
          e.currentTarget.src = '/images/default-thumbnail.jpeg';
        }}
      />
    );
  };

  const renderLinkContent = (url: string) => {
    if (!url) {
      return (
        <div className={styles.linkPreviewContainer}>
          <div className={styles.linkIcon}>
            <IconLink size={48} />
          </div>
          <p className={styles.errorText}>Invalid URL</p>
        </div>
      );
    }

    return (
      <div className={styles.linkPreviewContainer}>
        <div className={styles.linkContent}>
          <IconLink size={48} className={styles.linkIcon} />
          <TextButton
            text="Visit Link"
            right={<IconExternalLink size={20} />}
            underline
            onClick={() => window.open(url, '_blank')}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!submission.urls || submission.urls.length === 0) {
      return (
        <div className={styles.mediaContainer}>
          <div className={styles.linkPreviewContainer}>
            <div className={styles.linkIcon}>
              <IconLink size={48} />
            </div>
            <p className={styles.errorText}>No content available</p>
          </div>
        </div>
      );
    }

    // Handle link type submissions
    if (submission.type === 'link') {
      const url = (submission.urls as string[])[0];
      return <div className={styles.mediaContainer}>{renderLinkContent(url)}</div>;
    }

    const mediaUrls = submission.urls as MediaUrl[];

    // Handle single video or single image
    if (mediaUrls.length === 1) {
      return <div className={styles.mediaContainer}>{renderMediaContent(mediaUrls[0], 0)}</div>;
    }

    // Handle multiple images with carousel
    return (
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {mediaUrls.map((url, index) => (
            <div key={`${url.fileUrl}-${index}`} className={styles.emblaSlide}>
              <div className={styles.mediaContainer}>{renderMediaContent(url, index)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderComments = () => {
    if (!comments?.length) {
      return (
        <div className={styles.noComments}>
          <EmptyCommentsIcon height={155} width={192} />

          {isLoadingComments ? (
            <>
              <Skeleton
                variant="rectangular"
                width={200}
                height={14}
                sx={{
                  borderRadius: '10px',
                }}
              />
              <Skeleton
                variant="rectangular"
                width={150}
                height={14}
                sx={{
                  borderRadius: '10px',
                  marginTop: '8px',
                }}
              />
            </>
          ) : (
            <>
              {' '}
              <h5 className={styles.noCommentsTitle}>No Comments Yet...</h5>
              <p className={styles.noCommentsText}>
                Once &ldquo;Brand&rdquo; reviews your content, their feedback will be visible here.
              </p>
            </>
          )}
        </div>
      );
    }

    return <CommentThread comments={comments} isLoading={isLoadingComments} />;
  };

  return (
    <>
      <CustomModal
        show={isOpen}
        onClose={onClose}
        title={
          <div className={styles.modalHeader}>
            <div className={styles.modalHeaderLeft}>
              <h2>{submission.name}</h2>
              {submission.isResubmitted && <ResubmissionTag />}
            </div>
            {submission.stage !== 'approved' && (
              <div className={styles.modalHeaderRight}>
                <Button
                  variant="ghost"
                  onClick={() => setIsDeleteModalOpen(true)}
                  bgColor="transparent"
                  color="var(--dark-grey)"
                  border="1px solid var(--light-grey)"
                >
                  <TrashIcon color="var(--icon-default)" />
                </Button>
              </div>
            )}
          </div>
        }
        width={900}
        topCross={isMobileView === false}
        showDivider
        footer={
          submission?.stage === 'changesRequested' && (
            <div className={styles.buttonContainer}>
              <Button
                onClick={onClose}
                bgColor="transparent"
                color="var(--dark-grey)"
                border="1px solid var(--light-grey)"
              >
                Close
              </Button>
              <Button onClick={onResubmit} bgColor="var(--purple-p-400)">
                Resubmit Content
              </Button>
            </div>
          )
        }
        bodyClassName={styles.modalBodySection}
      >
        <div className={styles.viewSubmission}>
          <div className={styles.contentSection}>
            <div className={styles.mediaWrapper}>
              <div className={styles.carouselWrapper}>
                {submission.urls && submission.urls.length > 1 && submission.type !== 'link' && (
                  <div className={styles.imageCounter}>
                    {selectedIndex + 1} / {submission.urls.length}
                  </div>
                )}
                {renderContent()}
                {submission.urls && submission.urls.length > 1 && submission.type !== 'link' && (
                  <>
                    <button
                      type="button"
                      className={`${styles.carouselButton} ${styles.prevButton}`}
                      onClick={scrollPrev}
                      disabled={!prevBtnEnabled}
                    >
                      <IconChevronLeft size={24} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.carouselButton} ${styles.nextButton}`}
                      onClick={scrollNext}
                      disabled={!nextBtnEnabled}
                    >
                      <IconChevronRight size={24} />
                    </button>
                    <div className={styles.dotsContainer}>
                      {Array.from({ length: submission.urls.length }).map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`${styles.dot} ${index === selectedIndex ? styles.dotSelected : ''}`}
                          onClick={() => emblaApi && emblaApi.scrollTo(index)}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {comments && (comments?.length > 0 || submission?.stage !== 'approved') && (
            <div className={styles.commentsSection}>
              <h4>{comments.length > 0 ? `${comments.length}  Comment${comments.length > 1 ? 's' : ''}` : ''}</h4>
              {renderComments()}
            </div>
          )}
          {isMobileView && submission?.stage !== 'changesRequested' && (
            <div className={styles.mobileCloseButton}>
              <ButtonComponent variant="secondary" onClick={onClose} style={{ width: '100%' }}>
                Close
              </ButtonComponent>
            </div>
          )}
        </div>
      </CustomModal>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteSubmissionMutation.isPending}
      />
    </>
  );
}
